/**
 * Chat Panel - Live order chat within the admin Order Detail view.
 *
 * GlassCard container with message list, typing indicator, and input.
 * Uses CometChat headless SDK via useOrderChat hook.
 * Only rendered when order is in 'picking' status and companion app is enabled.
 */

import React, { useRef, useEffect } from 'react';
import { Box, Typography, Badge, Chip } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import GlassCard from './GlassCard';
import ChatBubble from './ChatBubble';
import ChatInput from './ChatInput';
import { useOrderChat } from '../../../hooks/useOrderChat';
import { useMissionTheme } from '../theme';

interface ChatPanelProps {
  orderId: string;
}

export default function ChatPanel({ orderId }: ChatPanelProps) {
  const { mc } = useMissionTheme();
  const { messages, sendText, isTyping, unreadCount, clearUnread, isReady } = useOrderChat(orderId);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Clear unread when messages update while panel is visible
  useEffect(() => {
    if (unreadCount > 0) clearUnread();
  }, [messages.length, unreadCount, clearUnread]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  return (
    <GlassCard
      title="Order Comms"
      icon={
        <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}>
          <ChatIcon sx={{ fontSize: 18 }} />
        </Badge>
      }
      iconColor={mc.secondary}
      headerAction={
        !isReady ? (
          <Chip
            label="CONNECTING"
            size="small"
            sx={{ bgcolor: 'rgba(255, 149, 0, 0.15)', color: mc.warning, fontSize: '0.65rem', height: 22 }}
          />
        ) : (
          <Chip
            label="LIVE"
            size="small"
            sx={{ bgcolor: 'rgba(0, 255, 136, 0.15)', color: mc.success, fontSize: '0.65rem', height: 22 }}
          />
        )
      }
      sx={{ gridColumn: '1 / -1' }}
    >
      {/* Messages area */}
      <Box
        sx={{
          maxHeight: 360,
          minHeight: 120,
          overflowY: 'auto',
          mb: 1,
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: mc.border, borderRadius: 2 },
        }}
      >
        {messages.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 100 }}>
            <Typography variant="body2" sx={{ color: mc.textMuted, fontStyle: 'italic' }}>
              {isReady ? 'No messages yet. Start the conversation.' : 'Connecting to chat...'}
            </Typography>
          </Box>
        ) : (
          messages.map((msg) => <ChatBubble key={msg.id} message={msg} />)
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Typing indicator */}
      {isTyping && (
        <Typography variant="caption" sx={{ color: mc.textMuted, fontStyle: 'italic', display: 'block', mb: 0.5 }}>
          Picker is typing...
        </Typography>
      )}

      {/* Input */}
      <ChatInput onSend={sendText} disabled={!isReady} />
    </GlassCard>
  );
}
